import { Injectable } from '@angular/core';
import { HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PhaseSelectorApiModel } from '@app/api/api-models/form-instance/PhaseSelectorApiModel';
import {
  PendingTaskApiModel,
  CreatePendingTaskApiModel,
  AssignmentSelectorsApiModel,
  DuplicatePendingTasksRequestApiModel,
  DuplicatePendingTasksApiModel,
  CompletedActivityMatchApiModel
} from '@app/api/api-models/pending-tasks';
import { HttpClientService } from '@app/api/api-services/http-client.service';

@Injectable()
export class PendingTasksService {
  public apiUrl = 'pending-tasks';

  constructor(private readonly http: HttpClientService) {}

  public getListActive(programRoleId: string, phases: PhaseSelectorApiModel[]): Observable<PendingTaskApiModel[]> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });

    return this.http
      .get<PendingTaskApiModel[]>(`${this.apiUrl}/get-list-active`, { params: params })
      .pipe(map((array) => array.map((x) => new PendingTaskApiModel().deserialize(x, phases))));
  }

  public getListActiveForMultiple(phases: PhaseSelectorApiModel[]): Observable<PendingTaskApiModel[]> {
    return this.http
      .get<PendingTaskApiModel[]>(`${this.apiUrl}/get-list-active-multiple`)
      .pipe(map((array) => array.map((x) => new PendingTaskApiModel().deserialize(x, phases))));
  }

  public getListCompleted(programRoleId: string, phases: PhaseSelectorApiModel[]): Observable<PendingTaskApiModel[]> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });

    return this.http
      .get<PendingTaskApiModel[]>(`${this.apiUrl}/get-list-completed`, { params: params })
      .pipe(map((array) => array.map((x) => new PendingTaskApiModel().deserialize(x, phases))));
  }

  public create(model: CreatePendingTaskApiModel): Observable<PendingTaskApiModel> {
    return this.http.post<PendingTaskApiModel>(`${this.apiUrl}/create`, model);
  }

  public delete(ids: number[]): Observable<void> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: ids
    };

    return this.http.delete<void>(`${this.apiUrl}/delete`, options);
  }

  public getActiveSelectors(programRoleId: string): Observable<AssignmentSelectorsApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });

    return this.http
      .get<AssignmentSelectorsApiModel>(`${this.apiUrl}/active-selectors`, { params: params })
      .pipe(map((response) => new AssignmentSelectorsApiModel().deserialize(response)));
  }

  public getCompletedSelectors(programRoleId: string): Observable<AssignmentSelectorsApiModel> {
    const params = new HttpParams({
      fromObject: {
        programRoleId: programRoleId
      }
    });
    return this.http
      .get<AssignmentSelectorsApiModel>(`${this.apiUrl}/completed-selectors`, { params: params })
      .pipe(map((response) => new AssignmentSelectorsApiModel().deserialize(response)));
  }

  public checkDuplicates(model: DuplicatePendingTasksRequestApiModel): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl}/check-duplicates`, model);
  }

  public getActivityDuplicates(
    model: DuplicatePendingTasksRequestApiModel
  ): Observable<DuplicatePendingTasksApiModel[]> {
    return this.http.post<DuplicatePendingTasksApiModel[]>(`${this.apiUrl}/get-activity-duplicates`, model);
  }

  public getBestCompletedMatch(
    model: DuplicatePendingTasksRequestApiModel
  ): Observable<CompletedActivityMatchApiModel> {
    return this.http.post<CompletedActivityMatchApiModel>(`${this.apiUrl}/completed-match`, model);
  }
}
